/**
 * M7 — PG Refund Tracker (admin)
 * ================================
 *
 * Tracking-only refund management. No PG API calls — accounts team
 * processes gateway refunds out-of-band and stamps the PG reference
 * back here. SLA is 24 h on `pending_gateway` (configurable backend-side).
 *
 * Sections:
 *   • Stats strip (5 status counts + amounts + SLA breach)
 *   • Filter bar (status / user-type / search / SLA-breached toggle)
 *   • Table (booking · PNR · user · amount · status · SLA · view)
 *   • Side drawer with smart action panel:
 *       - mark-processed (PG ref + provider)
 *       - mark-failed   (reason)
 *       - add-note      (audit-only)
 *
 * Theme matches the rest of the admin panel (#1B375B navy, #659A9B teal).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  IndianRupee, Search, RefreshCw, Filter, AlertTriangle, CheckCircle,
  Clock, X, ChevronRight, ShieldAlert, Banknote, Loader2,
  Calendar, Mail, Phone, Building, Receipt, FileText, Download,
} from 'lucide-react';
import axios from 'axios';
import { fmtIST, fmtISTShort } from '../../utils/datetimeIST';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/refunds`;

const STATUS_TONE = {
  pending_gateway: { bg: 'bg-amber-100',   text: 'text-amber-800',   label: 'Pending Gateway' },
  completed:       { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Completed' },
  failed:          { bg: 'bg-rose-100',    text: 'text-rose-800',    label: 'Failed' },
  manual_review:   { bg: 'bg-sky-100',     text: 'text-sky-800',     label: 'Manual Review' },
  no_refund:       { bg: 'bg-gray-100',    text: 'text-gray-700',    label: 'No Refund' },
};

const _money = (n) => {
  if (n === null || n === undefined) return '—';
  try { return `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }
  catch { return `₹${n}`; }
};

const _date = fmtIST;

function StatusBadge({ status }) {
  const t = STATUS_TONE[status] || STATUS_TONE.no_refund;
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${t.bg} ${t.text}`} data-testid={`refund-status-${status}`}>
      {t.label}
    </span>
  );
}

function SlaChip({ row }) {
  if (!row.sla_due_at) return null;
  if (row.sla_breached) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-800" data-testid="refund-sla-breached">
        <AlertTriangle className="w-3 h-3" />
        SLA breached
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800" data-testid="refund-sla-ok">
      <Clock className="w-3 h-3" />
      In SLA
    </span>
  );
}

// ─── Stats strip ──────────────────────────────────────────────────────
function StatsStrip({ stats, loading }) {
  if (!stats) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-20 rounded-xl bg-gray-100 animate-pulse" />
        ))}
      </div>
    );
  }
  const c = stats.counts || {};
  const a = stats.amounts || {};
  const cards = [
    { key: 'pending_gateway', label: 'Pending', tone: 'amber', value: c.pending_gateway || 0, amount: a.pending_gateway || 0 },
    { key: 'completed',       label: 'Completed', tone: 'emerald', value: c.completed || 0, amount: a.completed || 0 },
    { key: 'failed',          label: 'Failed', tone: 'rose', value: c.failed || 0, amount: a.failed || 0 },
    { key: 'manual_review',   label: 'Manual review', tone: 'sky', value: c.manual_review || 0, amount: a.manual_review || 0 },
    { key: 'no_refund',       label: 'No refund', tone: 'gray', value: c.no_refund || 0, amount: a.no_refund || 0 },
    { key: 'sla_breach',      label: 'SLA breached', tone: 'rose', value: stats.sla_breached || 0, amount: null, icon: ShieldAlert },
  ];
  const toneMap = {
    amber:   { bg: 'bg-amber-50',    border: 'border-amber-100',  text: 'text-amber-800',   icon: 'text-amber-600' },
    emerald: { bg: 'bg-emerald-50',  border: 'border-emerald-100',text: 'text-emerald-800', icon: 'text-emerald-600' },
    rose:    { bg: 'bg-rose-50',     border: 'border-rose-100',   text: 'text-rose-800',    icon: 'text-rose-600' },
    sky:     { bg: 'bg-sky-50',      border: 'border-sky-100',    text: 'text-sky-800',     icon: 'text-sky-600' },
    gray:    { bg: 'bg-gray-50',     border: 'border-gray-200',   text: 'text-gray-700',    icon: 'text-gray-500' },
  };
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4" data-testid="refunds-stats">
      {cards.map((card) => {
        const t = toneMap[card.tone];
        const Icon = card.icon || IndianRupee;
        return (
          <div key={card.key} className={`rounded-xl border ${t.bg} ${t.border} p-3`} data-testid={`refund-stat-${card.key}`}>
            <div className="flex items-center justify-between">
              <p className={`text-[10px] uppercase font-bold ${t.text}`}>{card.label}</p>
              <Icon className={`w-4 h-4 ${t.icon}`} />
            </div>
            <p className={`text-2xl font-extrabold ${t.text} mt-1`}>{loading ? '…' : card.value}</p>
            {card.amount !== null && (
              <p className="text-[10px] text-gray-500">{_money(card.amount)}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

const CANCEL_REASON_LABEL = {
  issuance_failure: 'Ticket failed to issue',
  post_ticket_cancel: 'Cancel after issuance',
  general: 'Support adjustment',
};

function GstListChip({ row }) {
  if (!row.has_tax_invoice && row.cancel_reason === 'issuance_failure') {
    return <span className="text-[10px] text-gray-400" title="No tax invoice">N/A</span>;
  }
  if (!row.has_tax_invoice) {
    return <span className="text-[10px] text-gray-400">—</span>;
  }
  if (row.credit_note_id || row.credit_note_status === 'issued') {
    return <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-700" title="Credit note issued"><FileText className="w-3 h-3" /> CN</span>;
  }
  if (row.credit_note_required || row.credit_note_status === 'pending') {
    return <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-amber-700" title="Credit note pending"><Receipt className="w-3 h-3" /> CN?</span>;
  }
  return <span className="inline-flex items-center gap-0.5 text-[10px] text-sky-700" title="Tax invoice"><Receipt className="w-3 h-3" /> Inv</span>;
}

// ─── Side drawer ──────────────────────────────────────────────────────
function RefundDrawer({ bookingId, onClose, onAction }) {
  const [hub, setHub] = useState(null);
  const [tab, setTab] = useState('summary');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [toast, setToast] = useState(null);
  const [processedModal, setProcessedModal] = useState(false);
  const [pgRef, setPgRef] = useState('');
  const [pgProvider, setPgProvider] = useState('razorpay');
  const [pgNote, setPgNote] = useState('');
  const [pgAmount, setPgAmount] = useState('');
  const [failedModal, setFailedModal] = useState(false);
  const [failReason, setFailReason] = useState('');
  const [noteInput, setNoteInput] = useState('');
  const [cnModal, setCnModal] = useState(false);
  const [cnForm, setCnForm] = useState({ cancel_fee_per_pax: '0', pax_count: '1', reason: '', airline_refund: '0' });
  const [cnSubmitting, setCnSubmitting] = useState(false);

  const data = hub?.refund;
  const payment = hub?.payment;
  const relatedTickets = hub?.support_tickets || [];

  const fetchOne = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/${bookingId}/hub`, { withCredentials: true });
      setHub(r.data);
    } catch (e) {
      setToast({ kind: 'err', text: e?.response?.data?.detail || 'Failed to load' });
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => { fetchOne(); }, [fetchOne]);

  if (!bookingId) return null;

  const doAction = async (path, body, label) => {
    setBusy(label);
    setToast(null);
    try {
      await axios.post(`${API}/${bookingId}/${path}`, body, { withCredentials: true });
      setToast({ kind: 'ok', text: `${label} ✓` });
      setProcessedModal(false);
      setFailedModal(false);
      setNoteInput('');
      setPgRef('');
      setPgNote('');
      setPgAmount('');
      setFailReason('');
      await fetchOne();
      if (onAction) onAction();
    } catch (e) {
      setToast({ kind: 'err', text: e?.response?.data?.detail || `${label} failed` });
    } finally {
      setBusy(null);
    }
  };

  const canProcess = data && ['pending_gateway', 'manual_review', 'failed'].includes(data.refund_status);
  const canFail = data && data.refund_status === 'pending_gateway';
  const gst = hub?.gst || {};
  const commission = hub?.commission || {};
  const refundLedger = hub?.refund_ledger || {};
  const txnEntries = hub?.transactions?.entries || [];
  const COMM_API = `${BACKEND_URL}/api/admin`;

  const downloadDoc = async (path, label) => {
    if (!path) return;
    try {
      const r = await axios.get(`${BACKEND_URL}${path}`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${label}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setToast({ kind: 'err', text: 'Download failed' });
    }
  };

  const submitCreditNote = async () => {
    const invId = gst.gst_invoice_id || data?.gst_invoice_id;
    if (!invId) {
      setToast({ kind: 'err', text: 'No tax invoice on this booking' });
      return;
    }
    setCnSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/invoices/${invId}/credit-note`,
        {
          cancel_fee_per_pax: parseFloat(cnForm.cancel_fee_per_pax || 0),
          pax_count: parseInt(cnForm.pax_count || 1, 10),
          reason: cnForm.reason || '',
          airline_refund: parseFloat(cnForm.airline_refund || 0),
        },
        { withCredentials: true },
      );
      setCnModal(false);
      setToast({ kind: 'ok', text: 'Credit note generated' });
      await fetchOne();
      if (onAction) onAction();
    } catch (e) {
      setToast({ kind: 'err', text: e?.response?.data?.detail || 'Credit note failed' });
    } finally {
      setCnSubmitting(false);
    }
  };

  const commissionAction = async (path, label, params = {}) => {
    setBusy(label);
    try {
      await axios.post(
        `${COMM_API}/bookings/${encodeURIComponent(bookingId)}/commission/${path}`,
        null,
        { withCredentials: true, params },
      );
      setToast({ kind: 'ok', text: `${label} ✓` });
      await fetchOne();
      if (onAction) onAction();
    } catch (e) {
      setToast({ kind: 'err', text: e?.response?.data?.detail || `${label} failed` });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-40" data-testid="refund-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full md:w-[680px] bg-white shadow-2xl overflow-y-auto">
        <header className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between z-10">
          <div>
            <p className="text-[10px] uppercase font-bold text-gray-500">Refund · Booking</p>
            <h2 className="text-lg font-bold text-[#1B375B]" data-testid="refund-drawer-booking">{data?.acf_booking_id || bookingId}</h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900" data-testid="refund-drawer-close">
            <X className="w-5 h-5" />
          </button>
        </header>

        {loading && (
          <div className="px-5 py-6 text-sm text-gray-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {toast && (
          <div className={`mx-5 mt-3 text-xs px-3 py-2 rounded-lg border ${toast.kind === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-rose-50 border-rose-200 text-rose-900'}`} data-testid="refund-toast">
            {toast.text}
          </div>
        )}

        {data && (
          <div className="px-5 py-4 space-y-4">
            <div className="flex flex-wrap gap-2 text-xs mb-2">
              <a href={`/admin/ops-monitor?focus=${encodeURIComponent(bookingId)}`} className="px-2 py-1 rounded border border-[#1B375B] text-[#1B375B] font-semibold">Live Monitor</a>
              {relatedTickets.map((tk) => (
                <a key={tk.id} href={`/admin/support/${tk.id}`} className="px-2 py-1 rounded border border-orange-300 text-orange-800 font-semibold">
                  {tk.reference} ({tk.status})
                </a>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={data.refund_status} />
              <SlaChip row={data} />
              {data.payment_mode_label && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-sky-100 text-sky-900" data-testid="refund-paid-via">
                  Paid via {data.payment_mode_label}
                </span>
              )}
              {data.refund_method && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                  Refund: {data.refund_method}
                </span>
              )}
              {data.pg_reference && (
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-900" data-testid="refund-pg-ref">
                  PG: {data.pg_reference}
                </span>
              )}
              {data.refund_transaction_id && (
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-violet-100 text-violet-900">
                  {data.refund_transaction_id}
                </span>
              )}
              {data.cancel_reason && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900" data-testid="refund-scenario-badge">
                  {CANCEL_REASON_LABEL[data.cancel_reason] || data.cancel_reason}
                </span>
              )}
            </div>

            <div className="flex gap-1 border-b border-gray-200" data-testid="refund-drawer-tabs">
              {['summary', 'transactions', 'gst'].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTab(t)}
                  className={`px-3 py-2 text-xs font-bold uppercase tracking-wide border-b-2 -mb-px ${tab === t ? 'border-[#1B375B] text-[#1B375B]' : 'border-transparent text-gray-500'}`}
                  data-testid={`refund-tab-${t}`}
                >
                  {t === 'summary' ? 'Summary' : t === 'transactions' ? 'Transactions' : 'GST'}
                </button>
              ))}
            </div>

            {tab === 'summary' && (
            <>
            {/* Money block */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                <p className="text-[10px] uppercase font-bold text-gray-500">Gross paid</p>
                <p className="text-lg font-extrabold text-gray-900">{_money(payment?.amount_paid ?? data.gross_paid)}</p>
                {payment?.paid_at && <p className="text-[10px] text-gray-500 mt-0.5">{_date(payment.paid_at)}</p>}
              </div>
              <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
                <p className="text-[10px] uppercase font-bold text-emerald-700">Refund initiated</p>
                <p className="text-lg font-extrabold text-emerald-900" data-testid="refund-amount">{_money(data.refund_initiated_amount ?? data.refund_amount)}</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                <p className="text-[10px] uppercase font-bold text-amber-700">Supplier fee</p>
                <p className="text-sm font-bold text-amber-900">{_money(data.supplier_fee)}</p>
              </div>
              <div className="bg-rose-50 rounded-lg p-3 border border-rose-100">
                <p className="text-[10px] uppercase font-bold text-rose-700">Service fee retained</p>
                <p className="text-sm font-bold text-rose-900">{_money(data.service_fee_retained)}</p>
              </div>
            </div>

            {/* Customer */}
            <div className="bg-white border border-gray-200 rounded-lg p-3 text-sm">
              <p className="text-[10px] uppercase font-bold text-gray-500 mb-2">Customer / Agent</p>
              <p className="font-semibold text-gray-900">{data.agency_name || data.contact_name || '—'}</p>
              {data.agency_name && data.contact_name && (
                <p className="text-xs text-gray-600">{data.contact_name}</p>
              )}
              <div className="flex flex-wrap gap-3 text-xs text-gray-600 mt-1">
                {data.contact_email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{data.contact_email}</span>}
                {data.contact_phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{data.contact_phone}</span>}
                <span className="inline-flex items-center gap-1"><Building className="w-3 h-3" />{data.user_type}</span>
                {data.pnr && <span className="font-mono">PNR: {data.pnr}</span>}
              </div>
              {data.cancel_reason && (
                <p className="text-xs text-amber-800 mt-2 font-medium">
                  Scenario: {CANCEL_REASON_LABEL[data.cancel_reason] || data.cancel_reason}
                </p>
              )}
              {data.reason && <p className="text-[11px] text-gray-500 mt-1">{data.reason}</p>}
            </div>

            {/* Timeline */}
            <div className="bg-white border border-gray-200 rounded-lg p-3 text-sm">
              <p className="text-[10px] uppercase font-bold text-gray-500 mb-2">Refund timeline</p>
              <ul className="space-y-1 text-xs text-gray-700">
                <li><Calendar className="w-3 h-3 inline mr-1" /><span className="font-semibold">Cancelled:</span> {_date(data.cancelled_at)} by {data.cancelled_by || '—'}</li>
                {data.refunded_at && <li><Calendar className="w-3 h-3 inline mr-1" /><span className="font-semibold">Refund initiated:</span> {_date(data.refunded_at)}</li>}
                {data.processed_at && <li className="text-emerald-700"><CheckCircle className="w-3 h-3 inline mr-1" /><span className="font-semibold">Processed:</span> {_date(data.processed_at)} by {data.processed_by}</li>}
                {data.failed_at && <li className="text-rose-700"><AlertTriangle className="w-3 h-3 inline mr-1" /><span className="font-semibold">Failed:</span> {_date(data.failed_at)} — {data.failed_reason}</li>}
                {data.sla_due_at && data.refund_status === 'pending_gateway' && (
                  <li className={data.sla_breached ? 'text-rose-700' : 'text-gray-600'}>
                    <Clock className="w-3 h-3 inline mr-1" />
                    <span className="font-semibold">SLA due:</span> {_date(data.sla_due_at)}
                  </li>
                )}
              </ul>
            </div>

            {/* Notes */}
            {data.refund_notes && data.refund_notes.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-3 text-sm" data-testid="refund-notes-list">
                <p className="text-[10px] uppercase font-bold text-gray-500 mb-2">Notes ({data.refund_notes.length})</p>
                <ul className="space-y-2">
                  {data.refund_notes.map((n, i) => (
                    <li key={i} className="text-xs text-gray-700 border-l-2 border-gray-200 pl-2">
                      <span className="text-gray-400">{_date(n.ts)}</span>{' '}
                      <span className="font-semibold uppercase text-[10px]">{n.kind}</span>{': '}
                      {n.text}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            </>
            )}

            {tab === 'transactions' && (
              <div className="bg-white border border-gray-200 rounded-lg p-3 text-sm" data-testid="refund-transactions-panel">
                <div className="flex justify-between text-xs text-gray-600 mb-2">
                  <span>Paid: <strong>{_money(hub?.transactions?.total_paid)}</strong></span>
                  <span>Refunded: <strong className="text-emerald-800">{_money(hub?.transactions?.total_refunded)}</strong></span>
                </div>
                {txnEntries.length === 0 ? (
                  <p className="text-xs text-gray-500 italic">No ledger entries found for this booking.</p>
                ) : (
                  <ul className="space-y-2">
                    {txnEntries.map((e, i) => (
                      <li key={i} className="flex justify-between gap-2 text-xs border-l-2 border-gray-200 pl-2">
                        <div>
                          <span className={`font-bold uppercase text-[10px] ${e.type === 'REFUNDED' ? 'text-emerald-700' : 'text-gray-700'}`}>{e.type}</span>
                          <span className="text-gray-500 ml-1">· {e.channel}</span>
                          <p className="text-gray-600 mt-0.5">{e.description}</p>
                          {e.reference && <p className="font-mono text-[10px] text-gray-400">{e.reference}</p>}
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-bold">{_money(e.amount)}</p>
                          <p className="text-[10px] text-gray-400">{_date(e.created_at)}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {tab === 'gst' && (
              <div className="space-y-3 text-sm" data-testid="refund-gst-panel">
                {gst.gst_note && (
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">{gst.gst_note}</div>
                )}
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <p className="text-[10px] uppercase font-bold text-gray-500 mb-2">Tax documents</p>
                  {gst.gst_invoice_id ? (
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => downloadDoc(gst.download_path, `tax_invoice_${gst.gst_invoice_number || 'invoice'}`)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold border border-gray-200 rounded hover:bg-gray-50"
                        data-testid="refund-download-invoice"
                      >
                        <Download className="w-3 h-3" /> Tax Invoice {gst.gst_invoice_number || ''}
                      </button>
                      {(gst.credit_note_id || data.credit_note_id) && (
                        <button
                          type="button"
                          onClick={() => downloadDoc(
                            gst.credit_note_download_path || `/api/admin/invoices/credit-notes/${gst.credit_note_id || data.credit_note_id}/download`,
                            `credit_note_${gst.credit_note_number || data.credit_note_number || 'cn'}`,
                          )}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold border border-emerald-200 text-emerald-800 rounded hover:bg-emerald-50"
                          data-testid="refund-download-credit-note"
                        >
                          <FileText className="w-3 h-3" /> Credit Note {gst.credit_note_number || data.credit_note_number || ''}
                        </button>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">No tax invoice on file.</p>
                  )}
                  {gst.can_generate_credit_note && (
                    <button
                      type="button"
                      onClick={() => setCnModal(true)}
                      className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 bg-[#1B375B] text-white text-xs font-semibold rounded-lg"
                      data-testid="refund-generate-credit-note"
                    >
                      <Receipt className="w-3 h-3" /> Generate credit note
                    </button>
                  )}
                </div>
                {commission.module_on && commission.accounting?.enabled && (
                  <div className="bg-white border border-violet-200 rounded-lg p-3" data-testid="refund-commission-panel">
                    <p className="text-[10px] uppercase font-bold text-violet-800 mb-2">Commission & TDS</p>
                    <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                      <span>Supplier gross: <strong>{_money(commission.accounting.supplier_commission_gross)}</strong></span>
                      <span>Agent net: <strong>{_money(commission.accounting.agent_commission_net_payable)}</strong></span>
                      <span>ACF retained: <strong>{_money(commission.accounting.acf_commission_retained_gross)}</strong></span>
                      <span>Status: <strong className="uppercase">{commission.accounting.status}</strong></span>
                    </div>
                    {commission.accounting.status === 'reversed' && (
                      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2" data-testid="refund-commission-reversed">
                        Commission ledger reversed on cancel — verify finance_ledger_entries REVERSAL rows.
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {commission.can_settle_supplier && (
                        <button type="button" disabled={!!busy} onClick={() => commissionAction('settle-supplier', 'Supplier settled')} className="px-2 py-1 text-xs font-semibold bg-violet-800 text-white rounded">
                          Post supplier receipt
                        </button>
                      )}
                      {commission.can_credit_agent && (
                        <>
                          <button type="button" disabled={!!busy} onClick={() => commissionAction('credit-agent', 'Accrued to books', { credit_wallet: false })} className="px-2 py-1 text-xs font-semibold border border-violet-800 text-violet-900 rounded">
                            Accrue to books
                          </button>
                          <button type="button" disabled={!!busy} onClick={() => commissionAction('credit-agent', 'Credited to agent', { credit_wallet: true })} className="px-2 py-1 text-xs font-semibold bg-emerald-800 text-white rounded">
                            Credit to agent
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
                {refundLedger.cap != null && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs" data-testid="refund-unified-ledger">
                    <p className="font-bold text-gray-700 mb-1">Unified refund cap</p>
                    <p>Cap {_money(refundLedger.cap)} · Refunded {_money(refundLedger.total_refunded)} · Remaining {_money(refundLedger.remaining_refundable)}</p>
                    {(refundLedger.wallet_refunds > 0 || refundLedger.credit_refunds > 0) && (
                      <p className="text-gray-500 mt-1">
                        Includes support {_money(refundLedger.ticket_payments_refunded)}
                        {refundLedger.wallet_refunds > 0 && ` + wallet ${_money(refundLedger.wallet_refunds)}`}
                        {refundLedger.credit_refunds > 0 && ` + credit ${_money(refundLedger.credit_refunds)}`}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
              <p className="text-[10px] uppercase font-bold text-gray-500">Accounts actions</p>
              {canProcess ? (
                <button
                  onClick={() => setProcessedModal(true)}
                  disabled={!!busy}
                  className="w-full inline-flex items-center justify-between px-3 py-2 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50"
                  data-testid="refund-act-mark-processed"
                >
                  <span className="inline-flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Mark as processed (stamp PG ref)</span>
                  <ChevronRight className="w-4 h-4 opacity-70" />
                </button>
              ) : (
                <p className="text-xs text-gray-500 italic">No process action available for status: {data.refund_status}</p>
              )}
              {canFail && (
                <button
                  onClick={() => setFailedModal(true)}
                  disabled={!!busy}
                  className="w-full inline-flex items-center justify-between px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-900 font-semibold text-sm hover:bg-rose-100 disabled:opacity-50"
                  data-testid="refund-act-mark-failed"
                >
                  <span className="inline-flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Mark as failed</span>
                  <ChevronRight className="w-4 h-4 opacity-70" />
                </button>
              )}
              <div className="pt-2 border-t border-gray-200">
                <p className="text-[10px] uppercase font-bold text-gray-500 mb-1">Add note</p>
                <textarea
                  rows={2}
                  value={noteInput}
                  onChange={(e) => setNoteInput(e.target.value)}
                  placeholder="Audit note (internal)…"
                  className="w-full px-2 py-1 border border-gray-200 rounded text-xs"
                  data-testid="refund-note-input"
                />
                <button
                  onClick={() => doAction('notes', { note: noteInput }, 'Note added')}
                  disabled={!noteInput || !!busy}
                  className="mt-1 px-3 py-1 bg-[#1B375B] text-white text-xs font-semibold rounded hover:bg-[#0f2742] disabled:opacity-50"
                  data-testid="refund-note-submit"
                >
                  Save note
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Mark-processed modal */}
        {processedModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="refund-processed-modal">
            <div className="absolute inset-0 bg-black/50" onClick={() => setProcessedModal(false)} />
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-5">
              <h3 className="text-lg font-bold text-[#1B375B] mb-1 inline-flex items-center gap-2"><CheckCircle className="w-5 h-5 text-emerald-600" /> Mark refund processed</h3>
              <p className="text-[11px] text-gray-500 mb-4">Stamp the PG reference + (optional) actual processed amount. Status flips to <strong>completed</strong>.</p>
              <div className="space-y-3 text-sm">
                <div>
                  <label className="text-[10px] uppercase font-semibold text-gray-500">PG reference *</label>
                  <input
                    value={pgRef}
                    onChange={(e) => setPgRef(e.target.value)}
                    placeholder="rfnd_NfgT9..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg font-mono text-sm"
                    data-testid="refund-pg-ref-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-semibold text-gray-500">PG provider</label>
                  <select
                    value={pgProvider}
                    onChange={(e) => setPgProvider(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    data-testid="refund-pg-provider"
                  >
                    <option value="razorpay">Razorpay</option>
                    <option value="ccavenue">CCAvenue</option>
                    <option value="bank">Bank Transfer</option>
                    <option value="upi">UPI</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase font-semibold text-gray-500">Processed amount (₹) — leave blank if equal to refund amount</label>
                  <input
                    type="number"
                    value={pgAmount}
                    onChange={(e) => setPgAmount(e.target.value)}
                    placeholder={data?.refund_amount || ''}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    data-testid="refund-pg-amount"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-semibold text-gray-500">Note (optional)</label>
                  <textarea
                    rows={2}
                    value={pgNote}
                    onChange={(e) => setPgNote(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    data-testid="refund-pg-note"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-5">
                <button onClick={() => setProcessedModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg">Cancel</button>
                <button
                  onClick={() => {
                    const body = { pg_reference: pgRef, pg_provider: pgProvider };
                    if (pgNote) body.note = pgNote;
                    if (pgAmount) body.processed_amount = Number(pgAmount);
                    doAction('mark-processed', body, 'Marked processed');
                  }}
                  disabled={!pgRef || pgRef.length < 2 || !!busy}
                  className="px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="refund-processed-confirm"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Mark-failed modal */}
        {failedModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="refund-failed-modal">
            <div className="absolute inset-0 bg-black/50" onClick={() => setFailedModal(false)} />
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-5">
              <h3 className="text-lg font-bold text-[#1B375B] mb-1 inline-flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-rose-600" /> Mark refund failed</h3>
              <p className="text-[11px] text-gray-500 mb-4">e.g. card expired, NACH bounce, customer didn&apos;t respond. Customer support will retry from the PG dashboard.</p>
              <div>
                <label className="text-[10px] uppercase font-semibold text-gray-500">Reason *</label>
                <textarea
                  rows={3}
                  value={failReason}
                  onChange={(e) => setFailReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  data-testid="refund-failed-reason"
                />
              </div>
              <div className="flex justify-end gap-2 mt-5">
                <button onClick={() => setFailedModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg">Cancel</button>
                <button
                  onClick={() => doAction('mark-failed', { reason: failReason }, 'Marked failed')}
                  disabled={!failReason || failReason.length < 2 || !!busy}
                  className="px-4 py-2 bg-rose-600 text-white text-sm font-semibold rounded-lg hover:bg-rose-700 disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="refund-failed-confirm"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        {cnModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="refund-cn-modal">
            <div className="absolute inset-0 bg-black/50" onClick={() => setCnModal(false)} />
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-5">
              <h3 className="text-lg font-bold text-[#1B375B] mb-3">Generate credit note</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <label className="text-[10px] uppercase font-semibold text-gray-500">Cancel fee / pax (₹)</label>
                  <input
                    type="number"
                    value={cnForm.cancel_fee_per_pax}
                    onChange={(e) => setCnForm((f) => ({ ...f, cancel_fee_per_pax: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-semibold text-gray-500">Passengers</label>
                  <input
                    type="number"
                    value={cnForm.pax_count}
                    onChange={(e) => setCnForm((f) => ({ ...f, pax_count: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-semibold text-gray-500">Airline refund (₹)</label>
                  <input
                    type="number"
                    value={cnForm.airline_refund}
                    onChange={(e) => setCnForm((f) => ({ ...f, airline_refund: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-semibold text-gray-500">Reason</label>
                  <textarea
                    rows={2}
                    value={cnForm.reason}
                    onChange={(e) => setCnForm((f) => ({ ...f, reason: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" onClick={() => setCnModal(false)} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
                <button
                  type="button"
                  onClick={submitCreditNote}
                  disabled={cnSubmitting}
                  className="px-4 py-2 bg-[#1B375B] text-white text-sm font-semibold rounded-lg disabled:opacity-50"
                >
                  {cnSubmitting ? 'Generating…' : 'Generate'}
                </button>
              </div>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────
export default function RefundsTracker() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ status: '', user_type: '', search: '', sla_breached_only: false, page: 1, limit: 25 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [err, setErr] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = { page: filters.page, limit: filters.limit };
      if (filters.status) params.status = filters.status;
      if (filters.user_type) params.user_type = filters.user_type;
      if (filters.search) params.search = filters.search;
      if (filters.sla_breached_only) params.sla_breached_only = true;
      const [listRes, statsRes] = await Promise.all([
        axios.get(API, { params, withCredentials: true }),
        axios.get(`${API}/stats`, { withCredentials: true }),
      ]);
      setRows(listRes.data.rows || []);
      setTotal(listRes.data.total || 0);
      setStats(statsRes.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load refunds');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('search');
    if (q) setFilters((f) => ({ ...f, search: q, page: 1 }));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / filters.limit)), [total, filters.limit]);

  const setFilter = (patch) => setFilters((f) => ({ ...f, ...patch, page: 1 }));

  return (
    <div className="px-4 py-5" data-testid="refunds-tracker">
      <header className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1B375B] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Banknote className="w-6 h-6 text-[#659A9B]" />
            Refund Tracker
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Track PG refunds across Razorpay / CCAvenue / Bank — accounts team stamps PG references here once processed.
          </p>
        </div>
        <button
          onClick={fetchData}
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1B375B] text-white text-sm font-semibold rounded-lg hover:bg-[#0f2742]"
          data-testid="refunds-refresh"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </header>

      <StatsStrip stats={stats} loading={loading} />

      <div className="bg-white border border-gray-200 rounded-xl p-3 mb-4">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search booking / PNR / email / phone / PG ref"
              value={filters.search}
              onChange={(e) => setFilter({ search: e.target.value })}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg"
              data-testid="refunds-search"
            />
          </div>
          <select
            value={filters.status}
            onChange={(e) => setFilter({ status: e.target.value })}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
            data-testid="refunds-status-filter"
          >
            <option value="">All status</option>
            {Object.keys(STATUS_TONE).map((s) => <option key={s} value={s}>{STATUS_TONE[s].label}</option>)}
          </select>
          <select
            value={filters.user_type}
            onChange={(e) => setFilter({ user_type: e.target.value })}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
            data-testid="refunds-usertype-filter"
          >
            <option value="">All users</option>
            <option value="b2c">B2C</option>
            <option value="agent">Agent</option>
          </select>
          <button
            onClick={() => setFilter({ sla_breached_only: !filters.sla_breached_only })}
            className={`inline-flex items-center gap-1 px-3 py-2 text-xs font-semibold rounded-lg border ${filters.sla_breached_only ? 'bg-rose-100 border-rose-200 text-rose-900' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
            data-testid="refunds-sla-filter"
          >
            <Filter className="w-3 h-3" /> SLA breached
          </button>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400 ml-auto" />}
        </div>
      </div>

      {err && (
        <div className="mb-3 p-3 rounded-lg border border-rose-200 bg-rose-50 text-sm text-rose-900" data-testid="refunds-error">
          {err}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="refunds-table">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase text-gray-600">
            <tr>
              <th className="text-left px-4 py-2.5 font-semibold">Booking · PNR</th>
              <th className="text-left px-3 py-2.5 font-semibold">Agency</th>
              <th className="text-left px-3 py-2.5 font-semibold">Paid via</th>
              <th className="text-right px-3 py-2.5 font-semibold">Refund amt</th>
              <th className="text-left px-3 py-2.5 font-semibold">Status · Method</th>
              <th className="text-center px-2 py-2.5 font-semibold">GST</th>
              <th className="text-left px-3 py-2.5 font-semibold">SLA</th>
              <th className="text-left px-3 py-2.5 font-semibold">Cancelled</th>
              <th className="text-right px-3 py-2.5 font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-gray-500">
                {loading ? 'Loading…' : 'No refunds match these filters.'}
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.booking_id} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelected(r.booking_id)}
                  data-testid={`refund-row-${r.booking_id}`}>
                <td className="px-4 py-2.5">
                  <p className="font-semibold text-gray-900 font-mono text-[12px]">{r.acf_booking_id || r.booking_id}</p>
                  {r.pnr && <p className="text-[10px] text-gray-500 font-mono">PNR · {r.pnr}</p>}
                </td>
                <td className="px-3 py-2.5">
                  <p className="text-gray-800 truncate max-w-[140px]">{r.agency_name || (r.user_type === 'agent' ? '—' : 'B2C')}</p>
                </td>
                <td className="px-3 py-2.5 text-xs text-gray-600">{r.payment_mode_label || r.payment_mode || '—'}</td>
                <td className="px-3 py-2.5 text-right font-bold text-emerald-800">{_money(r.refund_initiated_amount ?? r.refund_amount)}</td>
                <td className="px-3 py-2.5">
                  <div className="flex flex-col gap-0.5">
                    <StatusBadge status={r.refund_status} />
                    {r.refund_method && <span className="text-[10px] text-gray-500">via {r.refund_method}</span>}
                  </div>
                </td>
                <td className="px-2 py-2.5 text-center" data-testid={`refund-gst-col-${r.booking_id}`}><GstListChip row={r} /></td>
                <td className="px-3 py-2.5"><SlaChip row={r} /></td>
                <td className="px-3 py-2.5 text-xs text-gray-600">{_date(r.cancelled_at)}</td>
                <td className="px-3 py-2.5 text-right">
                  <button onClick={(e) => { e.stopPropagation(); setSelected(r.booking_id); }}
                          className="px-2 py-1 text-[11px] font-semibold text-[#1B375B] hover:bg-gray-100 rounded inline-flex items-center gap-1"
                          data-testid={`refund-view-${r.booking_id}`}>
                    Manage <ChevronRight className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-3 flex items-center justify-end gap-2 text-xs" data-testid="refunds-pagination">
          <button
            disabled={filters.page <= 1}
            onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
            className="px-3 py-1 rounded-md border border-gray-200 disabled:opacity-50 hover:bg-white"
            data-testid="refunds-page-prev"
          >Prev</button>
          <span className="px-2 font-semibold">{filters.page} / {totalPages}</span>
          <button
            disabled={filters.page >= totalPages}
            onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
            className="px-3 py-1 rounded-md border border-gray-200 disabled:opacity-50 hover:bg-white"
            data-testid="refunds-page-next"
          >Next</button>
        </div>
      )}

      {selected && (
        <RefundDrawer
          bookingId={selected}
          onClose={() => setSelected(null)}
          onAction={() => fetchData()}
        />
      )}
    </div>
  );
}
